import React, { useState, useEffect, memo } from "react";
import { useTypewriter, Cursor } from "react-simple-typewriter";
import { Download, ArrowUpRight } from "lucide-react";
import resume from "../assets/Resume.pdf";
import {
  typewriterWords,
  terminalLines,
  quickStats,
  heroContent,
  typewriterConfig,
  TERMINAL_CYCLE_MS,
} from "../config/home";
import { homeIcons } from "../utils/icons";
import { SHIMMER_BTN_CLASS, BTN_STATIC_CLASS } from "../constants/styles";
import { useReducedAnimations } from "../hooks/useReducedAnimations";

const MOBILE_WORD_CYCLE_MS = 3500;

const HeroButtons = ({ btnClass }) => (
  <div className="flex flex-row flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4">
    <a href={resume} className="hover:cursor-pointer z-100" download="Resume">
      <div className={btnClass} id="download-btn">
        Download Resume
        <Download className="ml-2" id="download-icon" />
      </div>
    </a>
    <a href="#projects" className="hover:cursor-pointer z-100">
      <div className={btnClass} id="view-work-btn">
        View my work
        <ArrowUpRight size={16} />
      </div>
    </a>
  </div>
);

const MobileRoleText = () => {
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % typewriterWords.length);
    }, MOBILE_WORD_CYCLE_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <h3 className="text-[30px] text-[paleturquoise]" id="profile">
      A{" "}
      <span className="text-[30px] text-[paleturquoise]" id="typewriter">
        {typewriterWords[wordIndex]}
      </span>
    </h3>
  );
};

const DesktopRoleText = () => {
  const [text] = useTypewriter({
    words: typewriterWords,
    loop: {},
    ...typewriterConfig,
  });

  return (
    <h3 className="text-[30px] text-[paleturquoise]" id="profile">
      A{" "}
      <span className="text-[30px] text-[paleturquoise]" id="typewriter">
        {text}
      </span>
      <span>
        <Cursor />
      </span>
    </h3>
  );
};

const TerminalPanel = memo(() => {
  const [lineIndex, setLineIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setLineIndex((prev) => (prev + 1) % terminalLines.length);
    }, TERMINAL_CYCLE_MS);
    return () => clearInterval(interval);
  }, []);

  const historyLines = terminalLines.slice(0, lineIndex).slice(-2);
  const activeLine = terminalLines[lineIndex].replace(/^>\s*/, "");

  return (
    <div className="w-full max-w-md mx-auto lg:max-w-none lg:w-[380px] shrink-0 hidden lg:block">
      <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden shadow-[0_0_60px_-15px_rgba(175,238,238,0.2)]">
        <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-[paleturquoise]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full bg-[#ff0044]/10 blur-3xl pointer-events-none" />

        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-black/30">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <span className="ml-2 text-xs text-gray-400 font-mono">
            {heroContent.terminalPrompt}
          </span>
        </div>

        <div className="p-5 min-h-[180px] font-mono text-sm relative bg-black/50">
          <p className="text-[paleturquoise] text-xs mb-4">{"// live build log"}</p>

          <div className="space-y-2 mb-3">
            {historyLines.map((line) => (
              <p key={line} className="text-gray-400 text-xs leading-relaxed">
                {line}
              </p>
            ))}
          </div>

          <p className="text-white text-[15px] font-medium leading-relaxed transition-opacity duration-300">
            <span className="text-[paleturquoise]">{"> "}</span>
            {activeLine}
            <span className="inline-block w-2 h-4 bg-[paleturquoise] ml-1 align-middle opacity-80" />
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 p-4 border-t border-white/10 bg-black/20">
          {quickStats.map(({ icon, label }) => {
            const Icon = homeIcons[icon];
            return (
              <div
                key={label}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-white/15 bg-white/[0.06]"
              >
                <Icon size={14} className="text-[paleturquoise] shrink-0" />
                <span className="text-xs font-semibold text-white">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

TerminalPanel.displayName = "TerminalPanel";

const Home = () => {
  const reducedAnimations = useReducedAnimations();
  const btnClass = reducedAnimations ? BTN_STATIC_CLASS : SHIMMER_BTN_CLASS;

  return (
    <div
      id="home"
      className="flex flex-col lg:flex-row items-center lg:items-center mb-16 mt-32 px-4 sm:px-6 lg:px-10 justify-center gap-12 lg:gap-16 w-full max-w-7xl mx-auto z-20 overflow-x-clip"
    >
      <div className="text-white w-full lg:flex-1 text-center lg:text-left">
        <h1 className="text-[45px] font-semibold text-[#ff0044]" id="greeting">
          {heroContent.greeting}
        </h1>
        <h2 className="text-[40px] font-bold" id="Name">
          {heroContent.headline}
        </h2>

        {reducedAnimations ? <MobileRoleText /> : <DesktopRoleText />}

        <p
          className="text-[18px] max-w-2xl mx-auto lg:mx-0 my-5 text-[lightgray] leading-relaxed"
          id="about-me"
        >
          {heroContent.bio}
        </p>

        <HeroButtons btnClass={btnClass} />
      </div>

      <TerminalPanel />
    </div>
  );
};

export default Home;
